import React from 'react';
import { Download, Search } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
  return data;
};

const ManagerClientTracksPage = () => {
  const { initData } = useTelegram();
  const [tracks, setTracks] = React.useState([]);
  const [query, setQuery] = React.useState('');
  const [tab, setTab] = React.useState('new');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;
    setLoading(true);
    setError('');
    try {
      const result = await apiPost('/api/manager/manual-tracks/list', { initData: initDataRaw });
      setTracks(Array.isArray(result?.tracks) ? result.tracks : []);
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить треки');
    } finally {
      setLoading(false);
    }
  }, [initData?.initData]);

  React.useEffect(() => {
    load();
  }, [load]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return tracks.filter((item) => {
      const tabOk = tab === 'processed' ? !!item.manual_processed : !item.manual_processed;
      const searchText = [item.track_number, item.client_code, item.full_name, item.phone, item.country]
        .join(' ')
        .toLowerCase();
      return tabOk && (!q || searchText.includes(q));
    });
  }, [tracks, query, tab]);

  const toggleProcessed = async (item) => {
    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;
    setError('');
    try {
      await apiPost('/api/manager/manual-tracks/process', {
        initData: initDataRaw,
        id: item.id,
        processed: !item.manual_processed,
      });
      await load();
    } catch (e) {
      setError(e?.message || 'Не удалось изменить отметку');
    }
  };

  const exportExcel = async () => {
    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;
    setError('');
    try {
      const res = await fetch(getApiPath('/api/manager/manual-tracks/export/excel'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ initData: initDataRaw }),
      });
      if (!res.ok) throw new Error('Не удалось сформировать Excel');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'client_tracks.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Ошибка экспорта');
    }
  };

  return (
    <div className="min-h-screen bg-bg text-text-primary pb-20">
      <div className="px-4 pt-6 max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Треки от клиентов</h1>
            <p className="mt-2 text-sm text-text-secondary">Треки, которые клиенты добавили самостоятельно.</p>
          </div>
          <button type="button" onClick={exportExcel} className="h-10 px-4 rounded-2xl border border-stroke bg-white/5 text-xs font-semibold flex items-center gap-2">
            <Download size={15} /> Excel
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button type="button" onClick={() => setTab('new')} className={`py-3 rounded-2xl border text-sm font-semibold ${tab === 'new' ? 'border-neon-yellow/40 bg-neon-yellow/10 text-neon-yellow' : 'border-stroke bg-surface'}`}>
            Новые ({tracks.filter((x) => !x.manual_processed).length})
          </button>
          <button type="button" onClick={() => setTab('processed')} className={`py-3 rounded-2xl border text-sm font-semibold ${tab === 'processed' ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300' : 'border-stroke bg-surface'}`}>
            Обработанные ({tracks.filter((x) => x.manual_processed).length})
          </button>
        </div>

        <div className="mt-4 relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Трек, код клиента, ФИО или телефон" className="w-full rounded-2xl border border-stroke bg-surface pl-11 pr-4 py-3 text-[16px] outline-none focus:border-white/25" />
        </div>

        {loading ? <div className="mt-4 text-sm text-text-secondary">Загрузка...</div> : null}
        {error ? <div className="mt-4 text-sm text-red-300">{error}</div> : null}

        <div className="mt-5 space-y-3">
          {filtered.map((item) => (
            <div key={item.id} className="rounded-2xl border border-stroke bg-surface p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-sm font-semibold">{item.track_number}</div>
                  <div className="mt-1 text-xs text-text-secondary">{item.created_at || ''}</div>
                </div>
                <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold">{item.country}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl border border-stroke bg-white/3 p-3"><div className="text-text-muted">Код клиента</div><div className="mt-1 font-semibold text-neon-yellow">{item.client_code || '-'}</div></div>
                <div className="rounded-xl border border-stroke bg-white/3 p-3"><div className="text-text-muted">Телефон</div><div className="mt-1">{item.phone || '-'}</div></div>
              </div>
              <div className="mt-2 text-sm text-text-secondary">{item.full_name || 'ФИО не указано'}</div>
              <button type="button" onClick={() => toggleProcessed(item)} className="mt-4 w-full py-3 rounded-2xl border border-stroke bg-white/5 text-sm font-semibold hover:bg-white/8 transition">
                {item.manual_processed ? 'Вернуть в новые' : 'Отметить обработанным'}
              </button>
            </div>
          ))}
          {!loading && filtered.length === 0 ? <div className="text-sm text-text-secondary">Список пуст</div> : null}
        </div>
      </div>
    </div>
  );
};

export default ManagerClientTracksPage;

